/**
 * outbox ของ event สถานะโต๊ะ (IndexedDB) — เก็บเฉพาะตัวที่ RTDB ยังไม่ตอบรับ
 *
 * - หนึ่งร้าน = หนึ่ง database (`xtable-tablestate:{storeId}`) แบบเดียวกับ stockTxDb.ts
 * - ไม่ถูกอ่านขึ้นจอ: ความจริงอยู่ที่ RTDB tableStatesEvents ที่เดียว
 *   outbox มีไว้ให้ของที่กดตอนเน็ตหลุดแล้วปิดแท็บไปก่อนเน็ตกลับ ไม่หาย (RTDB บนเว็บไม่มี disk cache)
 * - เปิด IndexedDB ไม่ได้ (private mode / ถูกปิด) -> ใช้หน่วยความจำแทน ทำงานได้แต่ไม่จำข้ามการเปิดแอป
 *
 * ไฟล์นี้ไม่ import อะไรที่เป็น runtime (เทสต์ใน node ใช้ memory outbox ได้ตรง ๆ)
 */

import type { TableStateEvent } from "../../../interface/TableStateEvent";

export interface TableStateOutbox {
  readonly kind: "indexeddb" | "memory";
  put(event: TableStateEvent): Promise<void>;
  remove(ids: string[]): Promise<void>;
  getAll(): Promise<TableStateEvent[]>;
  clear(): Promise<void>;
}

interface OutboxRow {
  id: string;
  savedAt: number;
  event: TableStateEvent;
}

const DB_PREFIX = "xtable-tablestate:";
const DB_VERSION = 1;
const OUTBOX = "outbox";

export const tableStateOutboxName = (storeId: string) => `${DB_PREFIX}${storeId}`;

const copy = <T>(value: T): T => JSON.parse(JSON.stringify(value));

// ---------------------------------------------------------------------------
// memory
// ---------------------------------------------------------------------------

export const createMemoryOutbox = (): TableStateOutbox => {
  const rows = new Map<string, OutboxRow>();
  return {
    kind: "memory",
    async put(event) {
      rows.set(event.id, { id: event.id, savedAt: Date.now(), event: copy(event) });
    },
    async remove(ids) {
      for (const id of ids) rows.delete(id);
    },
    async getAll() {
      return [...rows.values()].map((row) => copy(row.event));
    },
    async clear() {
      rows.clear();
    },
  };
};

// ---------------------------------------------------------------------------
// IndexedDB
// ---------------------------------------------------------------------------

const hasIndexedDb = () => typeof indexedDB !== "undefined";

const requestToPromise = <T>(request: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const transactionDone = (tx: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error("transaction aborted"));
  });

const openIndexedDb = (storeId: string): Promise<IDBDatabase | null> =>
  new Promise((resolve) => {
    try {
      if (!hasIndexedDb()) return resolve(null);
      const request = indexedDB.open(tableStateOutboxName(storeId), DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(OUTBOX)) db.createObjectStore(OUTBOX, { keyPath: "id" });
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });

const createIdbOutbox = (storeId: string, first: IDBDatabase): TableStateOutbox => {
  let connection: Promise<IDBDatabase | null> | null = null;

  // แท็บอื่นสั่งลบ / อัปเกรด database -> ปิดให้ แล้วคำสั่งถัดไปเปิดใหม่เอง
  const watch = (db: IDBDatabase) => {
    db.onversionchange = () => {
      db.close();
      connection = null;
    };
    db.onclose = () => {
      connection = null;
    };
    return db;
  };
  connection = Promise.resolve(watch(first));

  const conn = async (): Promise<IDBDatabase> => {
    if (!connection) connection = openIndexedDb(storeId).then((db) => (db ? watch(db) : null));
    const db = await connection;
    if (!db) {
      connection = null;
      throw new Error("table state outbox: เปิด IndexedDB ไม่ได้");
    }
    return db;
  };

  const write = async (fn: (store: IDBObjectStore) => void) => {
    const tx = (await conn()).transaction(OUTBOX, "readwrite");
    const done = transactionDone(tx);
    fn(tx.objectStore(OUTBOX));
    await done;
  };

  return {
    kind: "indexeddb",
    put: (event) =>
      write((store) => {
        store.put({ id: event.id, savedAt: Date.now(), event: copy(event) } as OutboxRow);
      }),
    remove: async (ids) => {
      if (ids.length === 0) return;
      await write((store) => {
        for (const id of ids) store.delete(id);
      });
    },
    async getAll() {
      const tx = (await conn()).transaction(OUTBOX, "readonly");
      const rows = (await requestToPromise(tx.objectStore(OUTBOX).getAll())) as OutboxRow[];
      return (rows || []).map((row) => row.event).filter(Boolean);
    },
    clear: () =>
      write((store) => {
        store.clear();
      }),
  };
};

const opened = new Map<string, Promise<TableStateOutbox>>();

/** outbox ของร้านนี้ (เปิดครั้งเดียวต่อร้าน)  เปิด IndexedDB ไม่ได้ = memory */
export const getTableStateOutbox = (storeId: string): Promise<TableStateOutbox> => {
  if (!storeId) return Promise.reject(new Error("table state outbox: ไม่มี storeId"));
  let outbox = opened.get(storeId);
  if (!outbox) {
    outbox = openIndexedDb(storeId).then((db) => {
      if (db) return createIdbOutbox(storeId, db);
      console.warn("[tableState] เปิด IndexedDB ไม่ได้ ใช้หน่วยความจำแทน (ปิดแท็บตอน offline แล้วของที่ยังไม่ขึ้นจะหาย)");
      return createMemoryOutbox();
    });
    opened.set(storeId, outbox);
  }
  return outbox;
};
